import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ManagerThemeProvider } from './context/ManagerThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetTpin from './pages/ResetTpin';
import Landing from './pages/Landing';
import {
  ManagerDashboardNew,
  ManagerMerchants,
  ManagerReports,
  ManagerEscrow,
  ManagerAccount,
  ManagerPayouts,
  ManagerCreditHistory,
  ManagerSettings,
} from './manager';
import {
  MerchantDashboard,
  MerchantAccount,
  MerchantPayouts,
  MerchantReports,
  MerchantNewPayouts,
  MerchantNewPayoutsPoolPay,
} from './merchant';
import { UserDashboard, UserAccount, UserActivity } from './pages/user';

// Role-based dashboard redirect component
const RoleBasedDashboard = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-600 to-violet-800 text-white text-lg">
        Loading...
      </div>
    );
  }

  // Redirect based on user role
  switch (user?.role) {
    case 'manager':
      return <Navigate to="/manager" replace />;
    case 'merchant':
      return <Navigate to="/merchant" replace />;
    case 'user':
      // For users with 'user' role, redirect to user dashboard
      return <Navigate to="/user" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ManagerThemeProvider>
        <Routes>
          {/* Landing page at root */}
          <Route path="/" element={<Landing />} />

          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-tpin" element={<ResetTpin />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Manager Dashboard */}
          <Route
            path="/manager"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerDashboardNew />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Merchants */}
          <Route
            path="/manager/merchants"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerMerchants />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Reports */}
          <Route
            path="/manager/reports"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerReports />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Escrow Transactions */}
          <Route
            path="/manager/escrow"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerEscrow />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Account */}
          <Route
            path="/manager/account"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerAccount />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Payouts */}
          <Route
            path="/manager/payouts"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerPayouts />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Merchant Report History */}
          <Route
            path="/manager/merchant-report-history"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerCreditHistory />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Manager Settings */}
          <Route
            path="/manager/settings"
            element={
              <ProtectedRoute requiredRole="manager">
                <RoleGuard allowedRoles={['manager', 'admin']}>
                  <ManagerSettings />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* User Dashboard */}
          <Route
            path="/user"
            element={
              <ProtectedRoute requiredRole="user">
                <RoleGuard allowedRoles={['user', 'admin']}>
                  <UserDashboard />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* User Account */}
          <Route
            path="/user/account"
            element={
              <ProtectedRoute requiredRole="user">
                <RoleGuard allowedRoles={['user', 'admin']}>
                  <UserAccount />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* User Activity */}
          <Route
            path="/user/activity"
            element={
              <ProtectedRoute requiredRole="user">
                <RoleGuard allowedRoles={['user', 'admin']}>
                  <UserActivity />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* Merchant Dashboard */}
          <Route
            path="/merchant"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantDashboard />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* Merchant Account */}
          <Route
            path="/merchant/account"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantAccount />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Merchant Payouts */}
          <Route
            path="/merchant/payouts"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantPayouts />
                </RoleGuard>
              </ProtectedRoute>
            }
          />

          {/* Merchant New Payouts */}
          <Route
            path="/merchant/new-payouts"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantNewPayouts />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/new-payouts/poolpay"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantNewPayoutsPoolPay />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* Merchant Reports */}
          <Route
            path="/merchant/reports"
            element={
              <ProtectedRoute requiredRole="merchant">
                <RoleGuard allowedRoles={['merchant', 'admin']}>
                  <MerchantReports />
                </RoleGuard>
              </ProtectedRoute>
            }
          />
          
          {/* 404 - Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ManagerThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
